/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useRef, useState } from "react"
import Exam from "./views/exam-room"
import Lobby from "./views/lobby"
import { VirtualExamContextProvider } from "views/exam/virtual-exam/virtual-exam-context"
import Switch, { Case, Default } from "react-switch-case"
import { useNavigate } from "react-router-dom"
import UIStore from "./stores/ui-store"
import { Paths } from "const"
import { observer } from "mobx-react-lite"
import CompatibilityCheckFailure from "@medaica/common/views/exam/virtual-exam/CompatibilityCheckFailure"
import CompatibilityCheck, { FailureReason } from "@medaica/common/components/compatibility-check"
import { ExamType } from "@medaica/common/const"
import AVStore from "@medaica/common/views/exam/virtual-exam/stores/av-store"
import MobileAppPage, { getMobilePlatform } from "./mobile-app-page"
import UnsupportedBrowserPage from "./unsupported-browser-page"

const Router = observer((): ReactElement => {
  const navigate = useNavigate()
  const uiStore = useRef(new UIStore(navigate, "lobby", Paths.home))
  const [compatibilityCheck, setCompatibilityCheck] = useState<{
    status: "inProgress" | "passed" | "failed"
    failureReason?: FailureReason
  }>({ status: "inProgress" })

  const handleCompatibilityCheckComplete = (passed: boolean, failureReason: FailureReason) => {
    setCompatibilityCheck(passed ? { status: "passed" } : { status: "failed", failureReason: failureReason })
  }

  // In a phone or tablet browser the exam can only run in the Medaica Patient app: the page offers to open
  // the exam in the app, or to install it.
  const mobilePlatform = getMobilePlatform(window.navigator)
  if (mobilePlatform) {
    return <MobileAppPage platform={mobilePlatform} />
  }

  // A computer browser other than Chrome or Edge can't run the exam and would dead-end in the "platform
  // unsupported" dialog; the patient gets help to continue in Edge or Chrome instead, at the same URL.
  // Media-permission failures on supported browsers still use the dialog flow below.
  if (!AVStore.isPlatformSupported()) {
    return <UnsupportedBrowserPage userAgent={window.navigator.userAgent} />
  }

  return (
    <Switch condition={compatibilityCheck.status}>
      <Case value="failed">
        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
        <CompatibilityCheckFailure failureReason={compatibilityCheck.failureReason!} />
      </Case>
      <Case value="passed">
        <VirtualExamContextProvider uiStore={uiStore.current}>
          <Switch condition={uiStore.current.view}>
            <Case value="exam">
              <Exam />
            </Case>
            <Default>
              <Lobby />
            </Default>
          </Switch>
        </VirtualExamContextProvider>
      </Case>
      <Default>
        <CompatibilityCheck examType={ExamType.Virtual} onComplete={handleCompatibilityCheckComplete} />
      </Default>
    </Switch>
  )
})

export default Router
